import { useEffect } from 'react'
import { normalizeEditorContent } from '../utils'

/**
 * Sync external value changes to the Trumbowyg editor
 * Prevents update loops by checking if content has actually changed
 */
export const useEditorSync = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
  isReady: boolean,
  // Set true while this hook is calling trumbowyg('html') so tbwchange ignores the resulting synthetic event
  isUpdatingRef: React.RefObject<boolean>,
  // Skip sync when true — editor already has the latest content and cursor position should be preserved
  isInternalChangeRef: React.RefObject<boolean>
) => {
  useEffect(() => {
    if (!isReady || !textareaRef.current || !window.$) return

    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false
      // Trumbowyg may not have synced its internal textarea yet when this runs
      // (e.g. cmd+backspace skips semanticCode entirely, leaving the textarea stale).
      // Compare against the live contentEditable innerHTML instead of trumbowyg('html')
      // so we don't mistake a stale textarea for a real external-value mismatch.
      const editorEl = textareaRef.current.parentElement?.querySelector(
        '.trumbowyg-editor'
      ) as HTMLElement | null
      if (normalizeEditorContent(editorEl?.innerHTML ?? '') === value) return
    }

    const currentHtml = window.$(textareaRef.current).trumbowyg('html')
    if (currentHtml !== value) {
      isUpdatingRef.current = true
      try {
        window.$(textareaRef.current).trumbowyg('html', value || '')
      } finally {
        isUpdatingRef.current = false
      }
    }
  }, [value, isReady, textareaRef, isUpdatingRef, isInternalChangeRef])
}
